"use client";

/**
 * LegalMock — animated mock of the askFinz Legal contract studio for
 * /apps/legal.
 *
 * Pure presentation: a three-pane layout — clause library (L), contract
 * body with two redline diffs in the centre (C), and a risk/obligation
 * panel (R) that fills item by item as the contract "loads".
 *
 * Trademark guardrail: no real party names, no real case citations. Use
 * placeholder names ("Acme Holdings", "Buyer", "Seller") and synthetic
 * citation IDs ("Smith v. Jones, 2019" — fictional). Lives in askfinz.com
 * only.
 */
import { useEffect, useRef, useState } from "react";
import {
  Scale,
  FileText,
  AlertTriangle,
  Calendar,
  Users,
  ShieldCheck,
  Quote,
  ArrowUpRight,
  BookOpen,
  Sparkles,
} from "lucide-react";

type ClauseGroup = { label: string; count: number; active?: boolean };

const CLAUSES: ClauseGroup[] = [
  { label: "Indemnity",      count: 14, active: true },
  { label: "Termination",    count: 9 },
  { label: "Confidentiality", count: 22 },
  { label: "IP assignment",  count: 7 },
  { label: "Governing law",  count: 31 },
  { label: "Limitation",     count: 18 },
];

type Risk = {
  icon: typeof AlertTriangle;
  level: "med" | "low" | "high";
  label: string;
  detail: string;
};

const RISKS: Risk[] = [
  { icon: Calendar,       level: "high", label: "Auto-renewal",     detail: "Renews 12 mo unless notice given ≥ 60 d before term" },
  { icon: AlertTriangle,  level: "med",  label: "Uncapped indemnity", detail: "Section 9.2 indemnity has no liability cap" },
  { icon: Users,          level: "low",  label: "Counterparty",       detail: "Acme Holdings · DE corp · in good standing" },
  { icon: ShieldCheck,    level: "med",  label: "Jurisdiction",       detail: "NY State law · SDNY exclusive forum" },
];

const RISK_TONE: Record<Risk["level"], string> = {
  high: "text-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 border-[hsl(var(--accent))]/30",
  med:  "text-foreground/80 bg-foreground/[0.05] border-foreground/15",
  low:  "text-foreground/65 bg-foreground/[0.02] border-foreground/10",
};

// Two diff blocks that fade in as the agent "drafts" them.
const REDLINES = [
  {
    title: "§ 4.1 Term",
    before: "This Agreement shall remain in effect for a period of three (3) years and shall automatically renew for successive one (1) year periods.",
    after:  "This Agreement shall remain in effect for a period of three (3) years. Renewal shall require mutual written consent at least sixty (60) days prior to expiration.",
  },
  {
    title: "§ 9.2 Indemnification",
    before: "Each party agrees to indemnify and hold harmless the other from any and all losses arising from such party's breach.",
    after:  "Each party's aggregate liability under this Section 9 shall not exceed the fees paid in the twelve (12) months preceding the claim, except for claims arising from gross negligence or wilful misconduct.",
  },
];

const INITIAL_RISKS = 1;
const INITIAL_REDLINES = 0;

export function LegalMock() {
  const [risksShown, setRisksShown] = useState(INITIAL_RISKS);
  const [redlinesShown, setRedlinesShown] = useState(INITIAL_REDLINES);
  const [selectedClause, setSelectedClause] = useState<string>(CLAUSES[0].label);
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const timers: number[] = [];
    const schedule = (fn: () => void, delay: number) => {
      timers.push(window.setTimeout(fn, delay));
    };

    const runCycle = () => {
      setRisksShown(INITIAL_RISKS);
      setRedlinesShown(INITIAL_REDLINES);
      setSelectedClause(CLAUSES[0].label);

      // Risks fill in one by one
      RISKS.forEach((_, i) => {
        if (i < INITIAL_RISKS) return;
        schedule(() => setRisksShown(i + 1), 500 + (i - INITIAL_RISKS) * 700);
      });

      // Then redlines appear
      REDLINES.forEach((_, i) => {
        schedule(() => setRedlinesShown(i + 1), 500 + RISKS.length * 700 + i * 1100);
      });

      // Then cycle clause selection through a couple options
      const afterRedlines = 500 + RISKS.length * 700 + REDLINES.length * 1100 + 800;
      ["Termination", "Indemnity"].forEach((label, i) => {
        schedule(() => setSelectedClause(label), afterRedlines + i * 900);
      });

      schedule(runCycle, afterRedlines + 3200);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        runCycle();
        obs.disconnect();
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            legal.askfinz.ai · live
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
            Demo
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[180px_1fr_240px] gap-px bg-foreground/10 min-h-[420px]">
          {/* Clause library */}
          <div className="bg-background p-3">
            <div className="flex items-center gap-1.5 mb-3 text-[10px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
              <BookOpen className="h-3 w-3" />
              Clauses
            </div>
            <ul className="space-y-1">
              {CLAUSES.map((c) => {
                const active = c.label === selectedClause;
                return (
                  <li key={c.label}>
                    <div
                      className={
                        "flex items-center justify-between rounded-md px-2 py-1.5 text-[11px] transition-all " +
                        (active
                          ? "bg-foreground text-background"
                          : "text-foreground/65 hover:bg-foreground/5")
                      }
                    >
                      <span className="truncate">{c.label}</span>
                      <span className={"font-mono " + (active ? "text-background/60" : "text-foreground/35")}>
                        {c.count}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Contract body with redlines */}
          <div className="bg-background p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5 text-foreground/65" />
                <span className="text-[10px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
                  MSA · Acme Holdings · v3.2
                </span>
              </div>
              <span className="font-mono text-[10px] text-foreground/65">12 pages</span>
            </div>

            {/* Preamble line — always visible */}
            <p className="text-[12px] leading-relaxed text-foreground/65 font-mono">
              <span className="text-foreground/65 mr-1.5 select-none">01</span>
              This MASTER SERVICES AGREEMENT is entered into between Acme
              Holdings (the &quot;Provider&quot;) and Buyer (the
              &quot;Customer&quot;) on the Effective Date.
            </p>

            <div className="mt-4 space-y-3">
              {REDLINES.slice(0, redlinesShown).map((r, i) => (
                <div
                  key={r.title}
                  className="rounded-lg border hairline bg-foreground/[0.015] overflow-hidden animate-[blur-fade-in_0.6s_ease-out]"
                >
                  <div className="px-3 py-1.5 border-b hairline flex items-center gap-2 bg-foreground/[0.02]">
                    <Quote className="h-3 w-3 text-foreground/65" />
                    <span className="text-[10px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
                      {r.title} · redline
                    </span>
                  </div>
                  <div className="px-3 py-2.5 space-y-1.5">
                    <p className="text-[11px] leading-relaxed line-through text-foreground/65">
                      {r.before}
                    </p>
                    <p className="text-[11px] leading-relaxed text-foreground/85 bg-[hsl(var(--accent))]/5 px-2 py-1 rounded">
                      {r.after}
                    </p>
                  </div>
                </div>
              ))}
              {redlinesShown < REDLINES.length && (
                <div className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono inline-flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-foreground/40 animate-pulse" />
                  Drafting suggestion against clause baseline
                </div>
              )}
            </div>
          </div>

          {/* Risk panel */}
          <div className="bg-background p-3">
            <div className="flex items-center gap-1.5 mb-3 text-[10px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
              <Scale className="h-3 w-3" />
              Risk + obligations
            </div>
            <ul className="space-y-2">
              {RISKS.slice(0, risksShown).map((r) => {
                const Icon = r.icon;
                return (
                  <li
                    key={r.label}
                    className={
                      "rounded-md border px-2.5 py-2 text-[11px] animate-[blur-fade-in_0.4s_ease-out] " +
                      RISK_TONE[r.level]
                    }
                  >
                    <div className="flex items-center gap-1.5">
                      <Icon className="h-3 w-3 shrink-0" />
                      <span className="font-medium truncate">{r.label}</span>
                      <span className="ml-auto text-[9px] tracking-[0.16em] uppercase font-mono opacity-70">
                        {r.level}
                      </span>
                    </div>
                    <p className="mt-1 text-[10px] leading-snug opacity-80">{r.detail}</p>
                  </li>
                );
              })}
              {risksShown < RISKS.length && (
                <li className="text-[10px] text-foreground/65 inline-flex items-center gap-1.5 px-1">
                  <Sparkles className="h-3 w-3 animate-pulse" />
                  Extracting…
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Footer band */}
        <div className="border-t hairline bg-foreground/[0.02] px-5 py-2.5 flex items-center justify-between">
          <span className="text-[10px] tracking-[0.18em] uppercase text-foreground/65 font-mono inline-flex items-center gap-1.5">
            <ShieldCheck className="h-3 w-3" />
            Citations · Smith v. Jones, 2019 · § 14(b)
          </span>
          <span className="font-mono text-[10px] text-foreground/65">Matter · MSA-2026-04-17</span>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.18em] text-foreground/65">
        <ArrowUpRight className="h-3 w-3" />
        <span>Coming soon to <span className="font-mono text-foreground/65">legal.askfinz.ai</span></span>
      </div>
    </div>
  );
}
